/**
 * /resolve — Post-install Resolution domain.
 *
 * Promoted from a sub-tab of /execution to its own first-class surface.
 * Aggregates the post-install exception triage views that previously lived
 * under `/execution?tab=resolve`:
 *   • Callbacks (QA last 7d)
 *   • Holdbacks & Disputes
 *   • Issues (operational)
 *   • Service Tickets
 *   • Friction (soft signals)
 *
 * Read-only landing — wraps existing canonical pages. No new resolvers,
 * writers, RBAC, or telemetry.
 *
 * URL: ?focus=callbacks|holdbacks|issues|service|friction (default: callbacks)
 */
import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import {
  PhoneCall,
  ShieldAlert,
  AlertTriangle,
  Wrench,
  Activity,
} from "lucide-react";
import { Card } from "@/components/ui/card";
import { useAppData } from "@/data/AppDataContext";
import { useRole } from "@/data/RoleContext";
import { canAccessExecutionDomain } from "@/data/executionDomain";
import { CockpitPage } from "@/components/cockpit/CockpitPage";
import { SubPivot } from "@/components/cockpit/SubPivot";
import { buildOperationalSummary } from "@/data/observability/operationalSummary";
import { useResolveServiceSummary } from "@/hooks/useResolveServiceServerPaging";
import { useResolveFocusSummary } from "@/hooks/useResolveServerPaging";
import { RESOLVE_SERVER_PAGING_ENABLED, RESOLVE_SERVICE_SERVER_PAGING_ENABLED } from "@/lib/dataRuntimeFlags";

import ExecutionQACallbacksPage from "@/pages/execution/ExecutionQACallbacksPage";
import ExecutionHoldbacksPage from "@/pages/execution/ExecutionHoldbacksPage";
import ExecutionFrictionPage from "@/pages/execution/ExecutionFrictionPage";
import IssuesListRoute from "@/pages/IssuesListRoute";
import ServiceTicketsPage from "@/pages/ServiceTicketsRoute";
import ResolveServiceTicketsServerPage from "@/pages/ResolveServiceTicketsServerPage";
import {
  ResolveCallbacksServerPage,
  ResolveFrictionServerPage,
  ResolveHoldbacksServerPage,
  ResolveIssuesServerPage,
} from "@/pages/resolve/ResolveServerFocusPages";

type FocusKey = "callbacks" | "holdbacks" | "issues" | "service" | "friction";
const FOCUS_ORDER: FocusKey[] = ["callbacks", "holdbacks", "issues", "service", "friction"];

export default function ResolveDomainLanding() {
  const { role } = useRole();
  const data = useAppData();
  const { disputeEvents, holdbackLedger } = data;
  const [params, setParams] = useSearchParams();
  const focusParam = params.get("focus") as string | null;
  const activeFocus: FocusKey =
    focusParam && (FOCUS_ORDER as string[]).includes(focusParam)
      ? (focusParam as FocusKey)
      : "callbacks";
  const resolveServerPagingEnabled = RESOLVE_SERVER_PAGING_ENABLED;
  const focusSummary = useResolveFocusSummary({ enabled: resolveServerPagingEnabled });
  const serviceSummary = useResolveServiceSummary({
    enabled: !resolveServerPagingEnabled && RESOLVE_SERVICE_SERVER_PAGING_ENABLED && activeFocus === "service",
  });

  const summary = useMemo(
    () =>
      buildOperationalSummary({
        jobs: data.jobs,
        contractors: data.contractors,
        callbacks: data.callbacks,
        holdbackLedger: data.holdbackLedger,
        disputeEvents: data.disputeEvents,
        softSuggestions: data.softSuggestions,
        overrideOutcomes: data.overrideOutcomes,
      }),
    [data.jobs, data.contractors, data.callbacks, data.holdbackLedger, data.disputeEvents, data.softSuggestions, data.overrideOutcomes],
  );

  const openDisputes = useMemo(() => {
    const opened = disputeEvents.filter((d) => d.type === "open").length;
    const closed = disputeEvents.filter(
      (d) => d.type === "withdraw" || d.type === "resolve",
    ).length;
    return Math.max(0, opened - closed);
  }, [disputeEvents]);

  const openHoldbacks = useMemo(() => {
    const created = holdbackLedger.filter((e) => e.action === "create").length;
    const closed = holdbackLedger.filter(
      (e) => e.action === "release" || e.action === "reverse",
    ).length;
    return Math.max(0, created - closed);
  }, [data.holdbackLedger]);
  const serviceCount = RESOLVE_SERVICE_SERVER_PAGING_ENABLED && activeFocus === "service"
    ? serviceSummary.data?.total ?? 0
    : data.serviceTickets.length;
  const callbacksCount = resolveServerPagingEnabled
    ? focusSummary.data?.callbacks7d ?? 0
    : summary.callbacks7d;
  const holdbacksCount = resolveServerPagingEnabled
    ? focusSummary.data?.holdbacksAttention ?? 0
    : openHoldbacks + openDisputes;
  const issuesCount = resolveServerPagingEnabled
    ? focusSummary.data?.issuesTotal ?? 0
    : data.issues.length;
  const resolvedServiceCount = resolveServerPagingEnabled
    ? focusSummary.data?.serviceTotal ?? 0
    : serviceCount;
  const frictionCount = resolveServerPagingEnabled
    ? focusSummary.data?.frictionTotal ?? 0
    : 0;

  if (!canAccessExecutionDomain(role)) {
    return (
      <div className="max-w-3xl mx-auto p-8">
        <Card className="p-6 text-sm text-muted-foreground">
          You don't have access to the Resolve domain.
        </Card>
      </div>
    );
  }

  const setFocus = (v: FocusKey) => {
    const next = new URLSearchParams(params);
    if (v === "callbacks") next.delete("focus");
    else next.set("focus", v);
    setParams(next, { replace: true });
  };

  const FOCUS_LABEL: Record<FocusKey, string> = {
    callbacks: "Callbacks",
    holdbacks: "Holdbacks",
    issues: "Issues",
    service: "Service",
    friction: "Friction",
  };

  return (
    <CockpitPage
      title="Resolve"
      titleSuffix={FOCUS_LABEL[activeFocus]}
      controls={<div id="resolve-masthead-controls" className="flex items-end gap-7 min-h-[36px]" />}
    >
      <SubPivot<FocusKey>
        value={activeFocus}
        onChange={setFocus}
        revealKey="resolve-focus"
        ariaLabel="Resolve focus"
        options={[
          {
            id: "callbacks",
            label: `Callbacks${callbacksCount ? ` · ${callbacksCount}` : ""}`,
            sub: "QA last 7d",
            icon: <PhoneCall className="h-3.5 w-3.5" />,
          },
          {
            id: "holdbacks",
            label: `Holdbacks${holdbacksCount ? ` · ${holdbacksCount}` : ""}`,
            sub: "ledger · disputes",
            icon: <ShieldAlert className="h-3.5 w-3.5" />,
          },
          {
            id: "issues",
            label: `Issues${issuesCount ? ` · ${issuesCount}` : ""}`,
            sub: "operational",
            icon: <AlertTriangle className="h-3.5 w-3.5" />,
          },
          {
            id: "service",
            label: `Service${resolvedServiceCount ? ` · ${resolvedServiceCount}` : ""}`,
            sub: "tickets",
            icon: <Wrench className="h-3.5 w-3.5" />,
          },
          {
            id: "friction",
            label: `Friction${frictionCount ? ` · ${frictionCount}` : ""}`,
            sub: "soft signals",
            icon: <Activity className="h-3.5 w-3.5" />,
          },
        ]}
      />
      {activeFocus === "callbacks" && (resolveServerPagingEnabled ? <ResolveCallbacksServerPage /> : <ExecutionQACallbacksPage embedded />)}
      {activeFocus === "holdbacks" && (resolveServerPagingEnabled ? <ResolveHoldbacksServerPage /> : <ExecutionHoldbacksPage embedded />)}
      {activeFocus === "issues" && (resolveServerPagingEnabled ? <ResolveIssuesServerPage /> : <IssuesListRoute />)}
      {activeFocus === "service" && (resolveServerPagingEnabled ? <ResolveServiceTicketsServerPage /> : <ServiceTicketsPage />)}
      {activeFocus === "friction" && (resolveServerPagingEnabled ? <ResolveFrictionServerPage /> : <ExecutionFrictionPage embedded />)}
    </CockpitPage>
  );
}
